import { useState } from "react";
import { CheckCircle2, Plus, Trash2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Reembolso() {
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    iban: "",
  });
  const [numerosCompra, setNumerosCompra] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { numero_processo }

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const updateNumero = (i, v) => {
    const next = [...numerosCompra];
    next[i] = v;
    setNumerosCompra(next);
  };
  const addNumero = () => setNumerosCompra((arr) => [...arr, ""]);
  const removeNumero = (i) =>
    setNumerosCompra((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const cleanNumeros = numerosCompra.map((s) => s.trim()).filter(Boolean);
    if (cleanNumeros.length === 0) {
      setError("Indique pelo menos um número de compra.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("nome", form.nome);
      fd.append("email", form.email);
      fd.append("telefone", form.telefone);
      fd.append("iban", form.iban);
      fd.append("numeros_compra", JSON.stringify(cleanNumeros));
      const { data } = await api.post("/reembolsos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess({ numero_processo: data.numero_processo });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
        <div className="ldr-card ldr-success p-8 sm:p-10 text-center" data-testid="reembolso-success">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full ldr-success-icon">
            <CheckCircle2 size={30} />
          </div>
          <h1 className="mt-6 text-2xl sm:text-3xl font-bold ldr-success-title">
            O seu pedido foi enviado com sucesso.
          </h1>
          <p className="mt-6 text-sm uppercase tracking-widest text-[var(--ldr-text-muted)]">
            Número do pedido
          </p>
          <p
            className="mt-2 text-3xl font-bold tracking-wide ldr-success-value"
            data-testid="reembolso-numero-processo"
          >
            {success.numero_processo}
          </p>
          <p className="mt-6 text-sm text-[var(--ldr-text-muted)] leading-relaxed max-w-md mx-auto">
            Enviámos uma confirmação para o email indicado. Guarde este número para futuras referências.
          </p>
          <button
            type="button"
            className="ldr-btn-outline mt-8"
            data-testid="reembolso-new-btn"
            onClick={() => {
              setSuccess(null);
              setForm({ nome: "", email: "", telefone: "", iban: "" });
              setNumerosCompra([""]);
              setAttachments([]);
            }}
          >
            Submeter novo pedido
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
      <header className="mb-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ldr-red)]">
          Formulário oficial
        </p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-[var(--ldr-text)]">
          Pedido de reembolso
        </h1>
        <p className="mt-3 text-[var(--ldr-text-muted)] leading-relaxed">
          Preencha os campos abaixo. Os campos assinalados com <span className="text-[var(--ldr-red)]">*</span> são obrigatórios.
        </p>
      </header>

      <form onSubmit={onSubmit} className="ldr-card p-6 sm:p-8 space-y-5" data-testid="reembolso-form">
        <div>
          <label className="ldr-label" htmlFor="nome">Nome <span className="text-[var(--ldr-red)]">*</span></label>
          <input
            id="nome"
            name="nome"
            required
            value={form.nome}
            onChange={onChange}
            className="ldr-input"
            placeholder="Nome completo"
            data-testid="reembolso-input-nome"
          />
        </div>

        <div>
          <label className="ldr-label" htmlFor="email">Email <span className="text-[var(--ldr-red)]">*</span></label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={form.email}
            onChange={onChange}
            className="ldr-input"
            placeholder="nome@exemplo.pt"
            data-testid="reembolso-input-email"
          />
        </div>

        <div>
          <label className="ldr-label" htmlFor="telefone">Telefone <span className="text-[var(--ldr-red)]">*</span></label>
          <input
            id="telefone"
            name="telefone"
            required
            value={form.telefone}
            onChange={onChange}
            className="ldr-input"
            placeholder="+351 900 000 000"
            data-testid="reembolso-input-telefone"
          />
        </div>

        <div>
          <label className="ldr-label">Número(s) de Compra <span className="text-[var(--ldr-red)]">*</span></label>
          <div className="space-y-2">
            {numerosCompra.map((val, i) => (
              <div key={i} className="flex gap-2">
                <input
                  type="text"
                  required
                  value={val}
                  onChange={(e) => updateNumero(i, e.target.value)}
                  className="ldr-input"
                  placeholder="Número de compra"
                  data-testid={`reembolso-input-numero-${i}`}
                />
                {numerosCompra.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeNumero(i)}
                    className="ldr-btn-outline !px-3"
                    aria-label="Remover"
                    data-testid={`reembolso-remove-numero-${i}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addNumero}
            className="mt-3 text-sm font-medium text-[var(--ldr-red)] inline-flex items-center gap-1 hover:text-[var(--ldr-red-hover)]"
            data-testid="reembolso-add-numero"
          >
            <Plus size={14} /> Adicionar outro número de compra
          </button>
          <p className="ldr-help">
            Pode indicar múltiplos números de compra se aplicável.
          </p>
        </div>

        <div>
          <label className="ldr-label" htmlFor="iban">IBAN <span className="text-[var(--ldr-red)]">*</span></label>
          <input
            id="iban"
            name="iban"
            required
            value={form.iban}
            onChange={onChange}
            className="ldr-input"
            placeholder="PT50 0000 0000 0000 0000 0000 0"
            data-testid="reembolso-input-iban"
          />
          <p className="ldr-help">O reembolso será transferido para este IBAN.</p>
        </div>

        {error && (
          <div
            className="text-sm text-[var(--ldr-red)] bg-[#FBEAEA] border border-[#F0CBCC] rounded-md px-4 py-3"
            data-testid="reembolso-error"
          >
            {error}
          </div>
        )}

        <div className="pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="ldr-btn-primary w-full sm:w-auto"
            data-testid="reembolso-submit"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting ? "A submeter…" : "Submeter pedido de reembolso"}
          </button>
        </div>
      </form>
    </div>
  );
}
